import db from './kioskDb';

// crypto.randomUUID() is only available in secure contexts (HTTPS / localhost).
// Kiosks on a local network over HTTP need a fallback.
function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback using crypto.getRandomValues (available in all modern browsers)
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
  );
}

// ============================================================
// CONFIG OPERATIONS
// ============================================================

export async function getConfig(key) {
  const row = await db.config.get(key);
  return row?.value ?? null;
}

export async function setConfig(key, value) {
  await db.config.put({ key, value });
}

export async function getAllConfig() {
  const rows = await db.config.toArray();
  return Object.fromEntries(rows.map((r) => [r.key, r.value]));
}

export async function deleteConfig(key) {
  await db.config.delete(key);
}

// ============================================================
// EMPLOYEE OPERATIONS
// ============================================================

export async function getAllEmployees() {
  return db.employees.toArray();
}

export async function getEmployeeById(id) {
  return db.employees.get(id);
}

export async function getEmployeeByPin(pin) {
  return db.employees.where('kioskPin').equals(pin).first();
}

export async function getEmployeeCount() {
  return db.employees.count();
}

/**
 * Bulk upsert employees from server data.
 * Accepts array of server employee objects and normalises them into local schema.
 */
export async function upsertEmployees(serverEmployees) {
  if (!serverEmployees?.length) return;

  const records = serverEmployees.map((e) => ({
    id: e.id,
    displayName: e.display_name || `${e.first_name || ''} ${e.last_name || ''}`.trim(),
    firstName: e.first_name || '',
    lastName: e.last_name || '',
    kioskPin: e.kiosk_pin || null,
    department: e.department || null,
    role: e.role || 'User',
    avatarUrl: e.avatar_url || null,
    avatarBlob: null, // populated separately by downloadAndCacheAvatars
    lastUpdated: new Date().toISOString(),
  }));

  await db.employees.bulkPut(records);
}

/**
 * Download avatar images and store as blobs in IndexedDB.
 * Non-fatal — failures are silently skipped.
 */
export async function downloadAndCacheAvatars(employees) {
  const toDownload = (employees || []).filter(
    (e) => e.avatarUrl && !e.avatarBlob
  );

  const results = await Promise.allSettled(
    toDownload.map(async (emp) => {
      try {
        const resp = await fetch(emp.avatarUrl);
        if (!resp.ok) return;
        const blob = await resp.blob();
        await db.employees.update(emp.id, { avatarBlob: blob });
      } catch {
        // Non-fatal — skip this avatar
      }
    })
  );

  return results;
}

/**
 * Clear all employees (used before full refresh).
 */
export async function clearEmployees() {
  await db.employees.clear();
}

// ============================================================
// CLOCK EVENT OPERATIONS
// ============================================================

/**
 * Add a new clock event to IndexedDB.
 * Returns the generated tempId for tracking.
 */
export async function addClockEvent({
  userId,
  action,
  method = null,
  hasSelfie = false,
  breakTypeId = null,
}) {
  const tempId = generateUUID();
  const now = new Date().toISOString();

  await db.clockEvents.add({
    tempId,
    userId,
    action, // 'clock_in' | 'clock_out' | 'break_start' | 'break_end'
    timestamp: now,
    method, // 'pin' | 'search' (clock_in only)
    hasSelfie,
    breakTypeId,
    syncStatus: 'pending',
    syncedAt: null,
    serverCheckinId: null,
    createdAt: now,
  });

  return { tempId, timestamp: now };
}

/**
 * Get all events with syncStatus = 'pending'.
 */
export async function getPendingEvents() {
  return db.clockEvents.where('syncStatus').equals('pending').toArray();
}

/**
 * Count of pending (unsynced) events.
 */
export async function getPendingCount() {
  return db.clockEvents.where('syncStatus').equals('pending').count();
}

/**
 * Mark events as synced/failed/conflict based on server results.
 * @param {Array} results - [{ temp_id, status: 'success'|'conflict'|'error', checkin_id? }]
 */
export async function markEventsSynced(results) {
  const now = new Date().toISOString();

  await db.transaction('rw', db.clockEvents, async () => {
    for (const r of results) {
      const event = await db.clockEvents.where('tempId').equals(r.temp_id).first();
      if (!event) continue;

      const updates = {
        syncStatus: r.status === 'success' ? 'synced' : r.status === 'conflict' ? 'conflict' : 'failed',
        syncedAt: r.status === 'success' ? now : null,
      };

      if (r.checkin_id) {
        updates.serverCheckinId = r.checkin_id;
      }

      await db.clockEvents.update(event.localId, updates);
    }
  });
}

/**
 * Get recent events for a specific user, ordered by timestamp desc.
 */
export async function getEventsForUser(userId, limit = 50) {
  return db.clockEvents
    .where('userId')
    .equals(userId)
    .reverse()
    .sortBy('timestamp')
    .then((events) => events.slice(0, limit));
}

/**
 * Find the active (open) clock-in for a user — a clock_in with no matching clock_out after it.
 * Returns the clock_in event or null.
 */
export async function getActiveClockIn(userId) {
  const userEvents = await db.clockEvents
    .where('userId')
    .equals(userId)
    .toArray();

  // Sort by timestamp ascending
  userEvents.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  // Walk through events to find the last unmatched clock_in
  let activeClockIn = null;
  for (const event of userEvents) {
    if (event.action === 'clock_in') {
      activeClockIn = event;
    } else if (event.action === 'clock_out') {
      activeClockIn = null;
    }
  }

  return activeClockIn;
}

/**
 * Find active break for a user — a break_start with no matching break_end after it.
 */
export async function getActiveBreak(userId) {
  const userEvents = await db.clockEvents
    .where('userId')
    .equals(userId)
    .toArray();

  userEvents.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  let activeBreak = null;
  for (const event of userEvents) {
    if (event.action === 'break_start') {
      activeBreak = event;
    } else if (event.action === 'break_end') {
      activeBreak = null;
    }
  }

  return activeBreak;
}

/**
 * Get all users currently on-site (have clock_in with no clock_out).
 * Returns array of { userId, displayName, firstName, lastName, avatarUrl, avatarBlob, department, clockInTime, durationMinutes }
 */
export async function getOnSiteUsers() {
  const allEvents = await db.clockEvents.toArray();

  // Group by userId, sort each group, find active clock-ins
  const byUser = {};
  for (const event of allEvents) {
    if (!byUser[event.userId]) byUser[event.userId] = [];
    byUser[event.userId].push(event);
  }

  const onSite = [];
  const now = Date.now();

  for (const [userId, events] of Object.entries(byUser)) {
    events.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    let activeClockIn = null;
    let activeBreak = null;
    for (const event of events) {
      if (event.action === 'clock_in') {
        activeClockIn = event;
        activeBreak = null; // new clock-in resets breaks
      } else if (event.action === 'clock_out') {
        activeClockIn = null;
        activeBreak = null;
      } else if (event.action === 'break_start') {
        activeBreak = event;
      } else if (event.action === 'break_end') {
        activeBreak = null;
      }
    }

    if (activeClockIn) {
      const employee = await getEmployeeById(userId);
      const clockInMs = new Date(activeClockIn.timestamp).getTime();
      onSite.push({
        userId,
        displayName: employee?.displayName || 'Unknown',
        firstName: employee?.firstName || '',
        lastName: employee?.lastName || '',
        avatarUrl: employee?.avatarUrl || null,
        avatarBlob: employee?.avatarBlob || null,
        department: employee?.department || null,
        checkInTime: activeClockIn.timestamp,
        durationMinutes: Math.round((now - clockInMs) / 60000),
        isOnBreak: !!activeBreak,
        activeBreak: activeBreak
          ? { id: activeBreak.tempId, startTime: activeBreak.timestamp, breakTypeId: activeBreak.breakTypeId }
          : null,
      });
    }
  }

  return onSite;
}

/**
 * Get all events for today (local timezone).
 */
export async function getTodayEvents() {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const startISO = startOfDay.toISOString();

  return db.clockEvents
    .where('timestamp')
    .aboveOrEqual(startISO)
    .toArray();
}

/**
 * Get weekly summary: { day: string, clockIns: number, clockOuts: number, breaks: number }[]
 */
export async function getWeekSummary() {
  const startOfWeek = new Date();
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const startISO = startOfWeek.toISOString();

  const events = await db.clockEvents
    .where('timestamp')
    .aboveOrEqual(startISO)
    .toArray();

  const days = {};
  for (const event of events) {
    const day = event.timestamp.slice(0, 10); // YYYY-MM-DD
    if (!days[day]) days[day] = { day, clockIns: 0, clockOuts: 0, breaks: 0 };
    if (event.action === 'clock_in') days[day].clockIns++;
    else if (event.action === 'clock_out') days[day].clockOuts++;
    else if (event.action === 'break_start') days[day].breaks++;
  }

  return Object.values(days).sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * Get events in a date range for export.
 */
export async function getEventsByDateRange(startDate, endDate) {
  const startISO = new Date(startDate).toISOString();
  const endISO = new Date(endDate).toISOString();

  return db.clockEvents
    .where('timestamp')
    .between(startISO, endISO, true, true)
    .toArray();
}

/**
 * Get total count of all events.
 */
export async function getTotalEventCount() {
  return db.clockEvents.count();
}

/**
 * Get count of synced events.
 */
export async function getSyncedEventCount() {
  return db.clockEvents.where('syncStatus').equals('synced').count();
}

// ============================================================
// SELFIE OPERATIONS
// ============================================================

/**
 * Store a selfie (base64 string) keyed by event tempId.
 */
export async function saveSelfie(tempId, data) {
  await db.selfies.put({ tempId, data });
}

/**
 * Get selfie data for a specific event.
 */
export async function getSelfie(tempId) {
  const row = await db.selfies.get(tempId);
  return row?.data ?? null;
}

/**
 * Delete selfies for events that have been synced.
 */
export async function deleteSyncedSelfies() {
  const syncedEvents = await db.clockEvents
    .where('syncStatus')
    .equals('synced')
    .toArray();

  const syncedTempIds = syncedEvents
    .filter((e) => e.hasSelfie)
    .map((e) => e.tempId);

  if (syncedTempIds.length > 0) {
    await db.selfies.bulkDelete(syncedTempIds);
  }

  return syncedTempIds.length;
}

/**
 * Count of selfies stored.
 */
export async function getSelfieCount() {
  return db.selfies.count();
}

// ============================================================
// BREAK TYPE OPERATIONS
// ============================================================

export async function getAllBreakTypes() {
  return db.breakTypes.toArray();
}

export async function upsertBreakTypes(serverBreakTypes) {
  if (!serverBreakTypes?.length) return;

  const records = serverBreakTypes.map((bt) => ({
    id: bt.id,
    companyId: bt.company_id,
    name: bt.name,
    isPaid: bt.is_paid ?? false,
    defaultDurationMinutes: bt.default_duration_minutes ?? null,
    sortOrder: bt.sort_order ?? 0,
    isActive: bt.is_active ?? true,
  }));

  await db.breakTypes.bulkPut(records);
}

// ============================================================
// SYNC LOG OPERATIONS
// ============================================================

export async function addSyncLog(entry) {
  return db.syncLog.add({
    timestamp: new Date().toISOString(),
    status: entry.status, // 'success' | 'partial' | 'failed'
    eventsTotal: entry.eventsTotal ?? 0,
    eventsSynced: entry.eventsSynced ?? 0,
    eventsFailed: entry.eventsFailed ?? 0,
    eventsConflict: entry.eventsConflict ?? 0,
    durationMs: entry.durationMs ?? 0,
    error: entry.error ?? null,
  });
}

export async function getRecentSyncLogs(limit = 10) {
  return db.syncLog.reverse().sortBy('timestamp').then((logs) => logs.slice(0, limit));
}

// ============================================================
// STORAGE ESTIMATES
// ============================================================

/**
 * Estimate total IndexedDB storage used by the kiosk.
 * Uses StorageManager API if available, falls back to counting records.
 */
export async function getStorageEstimate() {
  // Try StorageManager API (Chrome, Edge, Firefox)
  if (navigator.storage?.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      usedBytes: estimate.usage || 0,
      totalBytes: estimate.quota || 0,
      usedMB: Math.round((estimate.usage || 0) / (1024 * 1024) * 10) / 10,
      totalMB: Math.round((estimate.quota || 0) / (1024 * 1024)),
    };
  }

  // Fallback: count records
  const [eventCount, selfieCount, employeeCount] = await Promise.all([
    db.clockEvents.count(),
    db.selfies.count(),
    db.employees.count(),
  ]);

  return {
    usedBytes: null,
    totalBytes: null,
    usedMB: null,
    totalMB: null,
    eventCount,
    selfieCount,
    employeeCount,
  };
}

// ============================================================
// KIOSK ICON CACHING
// ============================================================

/**
 * Download kiosk icon images and store as blobs in IndexedDB.
 * Used by the dynamic manifest injection for offline PWA installs.
 * Non-fatal — failures are silently skipped.
 */
export async function downloadAndCacheKioskIcons(settings) {
  if (!settings) return;

  const urls = [
    { key: 'icon_192_blob', url: settings.icon_192_url },
    { key: 'icon_512_blob', url: settings.icon_512_url },
  ].filter((entry) => entry.url);

  if (!urls.length) return;

  await Promise.allSettled(
    urls.map(async ({ key, url }) => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) return;
        const blob = await resp.blob();
        await db.config.put({ key, value: blob });
      } catch {
        // Non-fatal — skip this icon
      }
    }),
  );
}

// ============================================================
// FULL DATABASE CLEAR (for unpair/reset)
// ============================================================

export async function clearAllData() {
  await Promise.all([
    db.config.clear(),
    db.employees.clear(),
    db.clockEvents.clear(),
    db.selfies.clear(),
    db.breakTypes.clear(),
    db.syncLog.clear(),
  ]);
}
